// Financing Events export filters — ported from OppGen
// (piq-customer-ui `models/spog/filters/FinancingEventsFilters.ts`). The keys
// mirror hermes' FinancingEventsFilters schema; covered-wagon converts them
// to snake_case (`OppGenQueries.ExportConfig` @financing_filter_keys).

export enum NULL_POLICY {
  INCLUDE = "include",
  EXCLUDE = "exclude",
  ONLY = "only",
}

// The absence of a policy is a distinct, filtering-nothing state.
export const NULL_POLICY_UNSET = "";

export const NULL_POLICY_OPTIONS = [
  { value: NULL_POLICY.EXCLUDE, label: "Exclude records with no value" },
  { value: NULL_POLICY.INCLUDE, label: "Include records with no value" },
  { value: NULL_POLICY.ONLY, label: "Only records with no value" },
];

export enum DISCOVERED_DATE_RANGE {
  ANY = "any",
  LAST_7_DAYS = "last_7_days",
  LAST_30_DAYS = "last_30_days",
  LAST_90_DAYS = "last_90_days",
  CUSTOM = "custom",
}

export const DISCOVERED_DATE_OPTIONS = [
  { value: DISCOVERED_DATE_RANGE.ANY, label: "Any time" },
  { value: DISCOVERED_DATE_RANGE.LAST_7_DAYS, label: "Last 7 days" },
  { value: DISCOVERED_DATE_RANGE.LAST_30_DAYS, label: "Last 30 days" },
  { value: DISCOVERED_DATE_RANGE.LAST_90_DAYS, label: "Last 90 days" },
  { value: DISCOVERED_DATE_RANGE.CUSTOM, label: "Custom range" },
];

export interface FinancingEventsFilters {
  financingType?: {
    include?: string[];
    exclude?: string[];
    nullPolicy?: NULL_POLICY;
  };
  amount?: {
    min?: number;
    max?: number;
    nullPolicy?: NULL_POLICY;
  };
  effectiveDate?: {
    start?: string;
    end?: string;
    nullPolicy?: NULL_POLICY;
  };
  discoveredDate?: {
    start?: string;
    end?: string;
  };
  investors?: {
    include?: string[];
  };
}

export const DEFAULT_FINANCING_EVENTS_FILTERS: FinancingEventsFilters = {};

const isEmptyValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/** Drops empty values and sections; `undefined` when nothing is left. */
export function pruneFilterSections<T extends object>(
  filters: T
): T | undefined {
  const pruned = Object.entries(filters).reduce<Record<string, unknown>>(
    (acc, [key, value]) => {
      if (isEmptyValue(value)) return acc;

      const inner = Object.entries(value as Record<string, unknown>).reduce<
        Record<string, unknown>
      >((innerAcc, [innerKey, innerValue]) => {
        if (isEmptyValue(innerValue)) return innerAcc;
        innerAcc[innerKey] = innerValue;
        return innerAcc;
      }, {});

      if (Object.keys(inner).length === 0) return acc;

      acc[key] = inner;
      return acc;
    },
    {}
  );

  return Object.keys(pruned).length === 0 ? undefined : (pruned as T);
}

export const pruneFinancingEventsFilters = (
  filters: FinancingEventsFilters
): FinancingEventsFilters | undefined => pruneFilterSections(filters);
